import { useState, useEffect, useRef } from 'react';
import { Wifi, WifiOff, RefreshCw, AlertTriangle } from 'lucide-react';
import { SyncQueueDialog } from './SyncQueueDialog';

interface ConnectionIndicatorProps {
  online: boolean;
  syncing?: boolean;
  pendingCount: number;
  /** Items that have failed too many times — surface as a warning badge */
  deadCount?: number;
}

type StatusType = 'online' | 'offline' | 'syncing';

export function ConnectionIndicator({ online, syncing, pendingCount, deadCount = 0 }: ConnectionIndicatorProps) {
  const status: StatusType = !online ? 'offline' : syncing ? 'syncing' : 'online';
  const [expanded, setExpanded] = useState(true);
  const [ghost, setGhost] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const prevStatusRef = useRef<StatusType>(status);
  const expandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ghostTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerExpand = () => {
    // Clear any pending timers
    if (expandTimerRef.current) clearTimeout(expandTimerRef.current);
    if (ghostTimerRef.current) clearTimeout(ghostTimerRef.current);

    // Show full pill
    setExpanded(true);
    setGhost(false);

    // Offline NEVER auto-collapses or ghosts. The user must stay
    // aware that they're working with cached data — fading the
    // indicator was making people think the app was just slow when
    // in reality there was no connection. Online/syncing keep the
    // 5s collapse → ghost behavior so they don't compete with the
    // rest of the UI.
    if (status === 'offline') return;

    // After 5s collapse to small pill
    expandTimerRef.current = setTimeout(() => {
      setExpanded(false);
      // After collapse animation finishes, go ghost
      ghostTimerRef.current = setTimeout(() => {
        setGhost(true);
      }, 500);
    }, 5000);
  };

  // On mount — show full for 5s
  useEffect(() => {
    triggerExpand();
    return () => {
      if (expandTimerRef.current) clearTimeout(expandTimerRef.current);
      if (ghostTimerRef.current) clearTimeout(ghostTimerRef.current);
    };
  }, []);

  // Re-expand on status change
  useEffect(() => {
    if (prevStatusRef.current !== status) {
      prevStatusRef.current = status;
      triggerExpand();
    }
  }, [status]);

  // Listen for the global "open sync dialog" event so toast actions and
  // any other surface in the app can open the inspector without having to
  // wire props through.
  useEffect(() => {
    const handler = () => setDialogOpen(true);
    window.addEventListener('iftmx:open-sync-dialog', handler);
    return () => window.removeEventListener('iftmx:open-sync-dialog', handler);
  }, []);

  const handleClick = () => {
    triggerExpand();
    // Open the queue inspector dialog so the user can see what is pending /
    // failing. Useful especially at the gym where the network drops a lot.
    if (pendingCount > 0 || deadCount > 0 || !online) {
      setDialogOpen(true);
    }
  };

  // Colors per status
  const colors = {
    online: {
      dot: ghost ? 'bg-green-400/30' : 'bg-green-400',
      border: ghost ? 'border-green-400/10' : 'border-green-400/40',
      bg: ghost ? 'bg-transparent' : 'bg-black/70',
      text: 'text-green-400',
      shadow: 'shadow-green-400/20',
    },
    syncing: {
      dot: ghost ? 'bg-yellow-400/30' : 'bg-yellow-400',
      border: ghost ? 'border-yellow-400/10' : 'border-yellow-400/40',
      bg: ghost ? 'bg-transparent' : 'bg-black/70',
      text: 'text-yellow-400',
      shadow: 'shadow-yellow-400/20',
    },
    offline: {
      dot: ghost ? 'bg-red-500/30' : 'bg-red-500',
      border: ghost ? 'border-red-500/10' : 'border-red-500/40',
      bg: ghost ? 'bg-transparent' : 'bg-black/70',
      text: 'text-red-400',
      shadow: 'shadow-red-500/20',
    },
  };

  const c = colors[status];

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <button
        onClick={handleClick}
        title={deadCount > 0 ? `${deadCount} cambio(s) requieren tu atención` : 'Estado de conexión'}
        className={`
          relative flex items-center gap-2 rounded-full border backdrop-blur-sm
          transition-all duration-500 ease-in-out overflow-hidden min-h-[36px] min-w-[36px]
          ${c.border} ${c.bg}
          ${expanded ? `px-3 py-1.5 shadow-lg ${c.shadow}` : 'p-1.5'}
        `}
      >
        {/* Dead-letter badge — orange dot in the corner if items need attention */}
        {deadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-orange-500 text-white flex items-center justify-center text-[9px] font-bold shadow-lg"
            aria-label={`${deadCount} cambios requieren atención`}
          >
            {deadCount > 9 ? '!' : deadCount}
          </span>
        )}
        {/* Status dot */}
        <div
          className={`
            rounded-full shrink-0 transition-all duration-300
            ${c.dot}
            ${expanded ? 'w-2 h-2' : 'w-2.5 h-2.5'}
            ${status === 'offline' ? 'animate-pulse' : ''}
            ${status === 'syncing' ? 'animate-pulse' : ''}
            ${status === 'online' && !expanded ? 'animate-pulse' : ''}
          `}
        />

        {/* Text — only when expanded */}
        <div
          className={`
            overflow-hidden transition-all duration-500
            ${expanded ? 'max-w-[160px] opacity-100' : 'max-w-0 opacity-0'}
          `}
        >
          <span
            className={`text-[11px] font-bold uppercase tracking-wider whitespace-nowrap ${c.text}`}
            style={{ fontFamily: "'Oswald', sans-serif" }}
          >
            {status === 'syncing' && (
              <span className="flex items-center gap-1.5">
                <RefreshCw className="w-3 h-3 animate-spin" />
                Sincronizando
              </span>
            )}
            {status === 'online' && (
              <span className="flex items-center gap-1.5">
                <Wifi className="w-3 h-3" />
                En línea
              </span>
            )}
            {status === 'offline' && (
              <span className="flex items-center gap-1.5">
                <WifiOff className="w-3 h-3" />
                Sin conexión
                {pendingCount > 0 && (
                  <span className="bg-red-500 text-white rounded-full px-1.5 py-0.5 text-[9px] leading-none ml-1">
                    {pendingCount}
                  </span>
                )}
              </span>
            )}
          </span>
        </div>
      </button>

      <SyncQueueDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}
